import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import type {
  ThemeAIJunkDecision,
  ThemeAIPersonalDataDecision,
  ThemeAIPersonalDataRecordsPage,
  ThemeAIScreening,
  ThemeAIScreeningEstimate,
  ThemeAIScreeningRecordsPage,
  ThemeAITask,
} from '../types/gotypes'

// True only for a 404: "not checked yet", and what a mixed-version deploy
// answers before the screening routes exist.
const isNotFound = (error: unknown): boolean => axios.isAxiosError(error) && error.response?.status === 404

/** A check whose model pass is still going; the page polls while this holds. */
export const isScreeningInFlight = (screening: ThemeAIScreening | undefined): boolean =>
  screening?.status === 'pending' || screening?.status === 'running'

interface UseThemeAIScreeningOptions {
  customer: string
  job: string
  taskId?: string
  task?: ThemeAITask
  /** The check's cost is only fetched when the offer or its dialog is on screen. */
  estimateEnabled?: boolean
}

// Durable junk-check controller for the Theme AI task page (#1891): the
// screening for the task's current data, what it would cost, and the user's
// choices on it. Every choice changes what a run will code, so each also
// refreshes the run estimate.
export const useThemeAIScreening = ({ customer, job, taskId, task, estimateEnabled }: UseThemeAIScreeningOptions) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const taskUrl = `${API_BASE_URL}/themeai/${customer}/${job}/tasks/${taskId}`
  const dataRevision = task?.data_revision
  const scope = { customer, job, taskId }
  const screeningKey = ['themeai_screening', { ...scope, dataRevision }]
  const ready = Boolean(customer && job && taskId && task && account.user)

  const screeningQuery = useQuery({
    queryKey: screeningKey,
    enabled: ready,
    queryFn: async (): Promise<ThemeAIScreening | null> => {
      try {
        const res = await axios.get<ThemeAIScreening>(`${taskUrl}/screening`)
        return res.data
      } catch (error) {
        if (isNotFound(error)) return null
        throw error
      }
    },
    // Every 2s while the model pass runs, as the runs list does.
    refetchInterval: (query) => (isScreeningInFlight(query.state.data ?? undefined) ? 2000 : false),
  })
  const screening = screeningQuery.data ?? undefined

  const estimateQuery = useQuery({
    queryKey: ['themeai_screening_estimate', { ...scope, dataRevision }],
    enabled: ready && Boolean(estimateEnabled),
    // A refusal (403) is an answer, not a blip: do not retry it.
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await axios.post<ThemeAIScreeningEstimate>(`${taskUrl}/screening/estimate`)
      return res.data
    },
  })

  // Same pattern as the runs hook: return the promise, so a caller that
  // awaits a mutation sees the state after the refresh.
  const invalidateAll = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['themeai_screening', scope], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_screening_records', scope], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_personal_data_records', scope], exact: false }),
    // A decision changes which answers a run codes, what it costs and how many remain uncoded.
    queryClient.invalidateQueries({ queryKey: ['themeai_estimate', scope], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_uncoded_count', scope], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['themeai_runs', scope], exact: false }),
  ])

  // POST /tasks/:id/screening: spends, so it is only ever a click.
  const startMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post<ThemeAIScreening>(`${taskUrl}/screening`)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // DELETE /tasks/:id/screening: cancels a running check, discards a finished one.
  const discardMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`${taskUrl}/screening`)
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // PUT /tasks/:id/screening/decision
  const decisionMutation = useMutation({
    mutationFn: async (decision: ThemeAIJunkDecision) => {
      const res = await axios.put<ThemeAIScreening>(`${taskUrl}/screening/decision`, { decision })
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // PATCH /tasks/:id/screening/records/:recordId. The switch flips at once on
  // the cached pages; a failure puts the server's answer back.
  const keptMutation = useMutation({
    mutationFn: async ({ recordId, kept }: { recordId: string; kept: boolean }) => {
      await axios.patch(`${taskUrl}/screening/records/${recordId}`, { kept })
    },
    onMutate: ({ recordId, kept }) => {
      queryClient.setQueriesData<ThemeAIScreeningRecordsPage>({ queryKey: ['themeai_screening_records', scope] }, (page) => page && {
        ...page,
        records: page.records.map((row) => (row.record_id === recordId ? { ...row, kept } : row)),
      })
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
      return invalidateAll()
    },
  })

  // PUT /tasks/:id/screening/personal-data/decision (#494)
  const personalDataDecisionMutation = useMutation({
    mutationFn: async (decision: ThemeAIPersonalDataDecision) => {
      const res = await axios.put<ThemeAIScreening>(`${taskUrl}/screening/personal-data/decision`, { decision })
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // PATCH /tasks/:id/screening/personal-data/records/:recordId, flipped at
  // once on the cached pages as the junk Keep is.
  const personalDataKeptMutation = useMutation({
    mutationFn: async ({ recordId, kept }: { recordId: string; kept: boolean }) => {
      await axios.patch(`${taskUrl}/screening/personal-data/records/${recordId}`, { kept })
    },
    onMutate: ({ recordId, kept }) => {
      queryClient.setQueriesData<ThemeAIPersonalDataRecordsPage>({ queryKey: ['themeai_personal_data_records', scope] }, (page) => page && {
        ...page,
        records: page.records.map((row) => (row.record_id === recordId ? { ...row, kept } : row)),
      })
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
      return invalidateAll()
    },
  })

  // GET /tasks/:id/screening/personal-data/export (#488): a CSV of the
  // flagged answers' ids and the kinds of data, never their text. Fetched
  // with the session's header and saved from a blob, so the sign-in token is
  // never put in a URL a log could keep.
  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.get(`${taskUrl}/screening/personal-data/export`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `themeai-${taskId}-personal-data.csv`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  const handleStartCheck = () => startMutation.mutateAsync()
  const handleDiscard = () => discardMutation.mutateAsync()
  const handleSetDecision = (decision: ThemeAIJunkDecision) => decisionMutation.mutateAsync(decision)
  const handleSetKept = (recordId: string, kept: boolean) => keptMutation.mutateAsync({ recordId, kept })
  const handleSetPersonalDataDecision = (decision: ThemeAIPersonalDataDecision) => personalDataDecisionMutation.mutateAsync(decision)
  const handleSetPersonalDataKept = (recordId: string, kept: boolean) => personalDataKeptMutation.mutateAsync({ recordId, kept })
  const handleDownloadPersonalData = () => exportMutation.mutateAsync()

  return {
    screening,
    screeningLoading: screeningQuery.isLoading,
    screeningInFlight: isScreeningInFlight(screening),

    estimate: estimateQuery.data,
    estimateLoading: estimateQuery.isLoading,

    handleStartCheck,
    startCheckLoading: startMutation.isPending,
    handleDiscard,
    discardLoading: discardMutation.isPending,
    handleSetDecision,
    setDecisionLoading: decisionMutation.isPending,
    handleSetKept,
    handleSetPersonalDataDecision,
    setPersonalDataDecisionLoading: personalDataDecisionMutation.isPending,
    handleSetPersonalDataKept,
    handleDownloadPersonalData,
    downloadPersonalDataLoading: exportMutation.isPending,
  }
}

export type ThemeAIScreeningController = ReturnType<typeof useThemeAIScreening>

export interface UseThemeAIScreeningRecordsOptions {
  category?: string
  limit?: number
  offset?: number
  enabled?: boolean
}

// GET /tasks/:id/screening/records: the flagged answers, a page at a time,
// optionally one category.
export const useThemeAIScreeningRecords = (
  customer: string,
  job: string,
  taskId: string | undefined,
  screeningId: string | undefined,
  { category, limit = 25, offset = 0, enabled = true }: UseThemeAIScreeningRecordsOptions = {},
) => {
  const account = useAccount()
  const query = useQuery({
    queryKey: ['themeai_screening_records', { customer, job, taskId, screeningId, category, limit, offset }],
    enabled: Boolean(enabled && customer && job && taskId && screeningId && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAIScreeningRecordsPage>(
        `${API_BASE_URL}/themeai/${customer}/${job}/tasks/${taskId}/screening/records`,
        { params: { category: category || undefined, limit, offset } },
      )
      return res.data
    },
    placeholderData: keepPreviousData,
  })

  return {
    records: query.data?.records || [],
    recordsTotal: query.data?.total || 0,
    recordsLoading: query.isLoading,
    recordsFetching: query.isFetching,
  }
}

// GET /tasks/:id/screening/personal-data (#494): the answers the check
// found personal data in, a page at a time, optionally one kind.
export const useThemeAIPersonalDataRecords = (
  customer: string,
  job: string,
  taskId: string | undefined,
  screeningId: string | undefined,
  { category, limit = 25, offset = 0, enabled = true }: UseThemeAIScreeningRecordsOptions = {},
) => {
  const account = useAccount()
  const query = useQuery({
    queryKey: ['themeai_personal_data_records', { customer, job, taskId, screeningId, category, limit, offset }],
    enabled: Boolean(enabled && customer && job && taskId && screeningId && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAIPersonalDataRecordsPage>(
        `${API_BASE_URL}/themeai/${customer}/${job}/tasks/${taskId}/screening/personal-data`,
        { params: { category: category || undefined, limit, offset } },
      )
      return res.data
    },
    placeholderData: keepPreviousData,
  })

  return {
    records: query.data?.records || [],
    recordsTotal: query.data?.total || 0,
    recordsFetching: query.isFetching,
  }
}

export default useThemeAIScreening
